import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Star, Gift, Bell, MessageCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const WHATSAPP_NUMBER = '919074305418';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POOJA_COLORS = {
  'thilahomam': '#C2410C',
  'archana': '#9A3412',
  'ganapathi-homam': '#B45309',
};

const POOJA_IMAGES = {
  'thilahomam': 'https://images.pexels.com/photos/12430842/pexels-photo-12430842.jpeg?auto=compress&cs=tinysrgb&w=600',
  'ganapathi-homam': 'https://customer-assets-4nw71qhi.emergentagent.net/job_pooja-booking-8/artifacts/6f71a11777e581c7_ganpathy-homam-2.jpg',
  'archana': 'https://customer-assets-4nw71qhi.emergentagent.net/job_pooja-booking-8/artifacts/cd08d18468fc3c20_archana.jpeg'
};

export default function SubscriptionsPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [expandedCard, setExpandedCard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/subscription-plans`).then(res => {
      setPlans(res.data.plans || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleBookNow = (plan) => {
    const msg = `Namaskaram,\n\nI would like to subscribe to the *${plan.title} Annual Subscription*.\n\nPlan Details:\n- Pay for ${plan.paid_months} months, get ${plan.bonus_months} bonus months FREE\n- Total: ${plan.total_months} months\n- Annual Price: ₹${plan.annual_price.toLocaleString()}\n\nPlease share the payment details and booking confirmation.\n\nThank you!`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="animate-spin h-8 w-8 border-4 rounded-full" style={{ borderColor: '#C2410C', borderTopColor: 'transparent' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
      {/* Header */}
      <div className="relative overflow-hidden" style={{ backgroundColor: '#1C1917' }}>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 30% 50%, #C2410C 0%, transparent 60%)' }}></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-4 py-10 sm:py-14">
          <button onClick={() => navigate('/')} className="flex items-center gap-1 text-white/60 hover:text-white text-sm mb-6 transition-colors" data-testid="back-to-home">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </button>
          <div className="text-center space-y-3">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold" style={{ backgroundColor: '#C2410C', color: 'white' }}>
              <Gift className="h-4 w-4" /> Annual Plans
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white" style={{ fontFamily: '"Cormorant Garamond", serif' }} data-testid="subs-heading">
              Annual Pooja Subscriptions
            </h1>
            <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto">
              Pay for 12 months and get bonus months FREE. Monthly poojas performed at the temple on your behalf.
            </p>
          </div>
        </div>
      </div>

      {/* Subscription Cards */}
      <div className="max-w-5xl mx-auto px-4 py-10">
        {plans.length === 0 ? (
          <div className="text-center py-16" data-testid="no-plans">
            <Gift className="mx-auto h-12 w-12 mb-4" style={{ color: '#D6D3D1' }} />
            <p className="text-lg font-medium" style={{ color: '#78716C' }}>Subscription plans coming soon</p>
            <p className="text-sm mt-2" style={{ color: '#A8A29E' }}>Check back later for our annual pooja subscription offers</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => {
              const color = POOJA_COLORS[plan.pooja_type] || '#C2410C';
              const image = POOJA_IMAGES[plan.pooja_type] || '';
              const isExpanded = expandedCard === plan.id;

              return (
                <div key={plan.id} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col" data-testid={`sub-card-${plan.id}`}>
                  {/* Image + Offer Badge */}
                  <div className="relative h-44 overflow-hidden">
                    {image && <img src={image} alt={plan.title} className="w-full h-full object-cover" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                    <div className="absolute top-3 right-3">
                      {plan.savings_pct > 0 && (
                        <span className="px-3 py-1.5 rounded-full text-xs font-bold text-white shadow-lg" style={{ backgroundColor: color }} data-testid={`offer-badge-${plan.id}`}>
                          Save {plan.savings_pct}%
                        </span>
                      )}
                    </div>
                    <div className="absolute bottom-3 left-4">
                      <h3 className="text-white text-xl font-bold">{plan.title}</h3>
                      <p className="text-white/70 text-sm">{plan.title_ml}</p>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5 flex-1 flex flex-col">
                    <p className="text-sm leading-relaxed mb-4" style={{ color: '#57534E' }}>{plan.description}</p>

                    {/* Pricing */}
                    <div className="rounded-xl p-4 mb-4" style={{ backgroundColor: `${color}08`, border: `1px solid ${color}20` }}>
                      {plan.monthly_price > 0 ? (
                        <>
                          <div className="flex items-baseline gap-2 mb-1">
                            <span className="text-2xl font-bold" style={{ color }}>₹{plan.annual_price.toLocaleString()}</span>
                            <span className="text-sm" style={{ color: '#78716C' }}>/year</span>
                          </div>
                          {plan.original_price > plan.annual_price && (
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-sm line-through" style={{ color: '#A8A29E' }}>₹{plan.original_price.toLocaleString()}</span>
                              <span className="text-sm font-semibold" style={{ color: '#059669' }}>You save ₹{plan.savings.toLocaleString()}</span>
                            </div>
                          )}
                          <div className="text-xs" style={{ color: '#78716C' }}>₹{plan.monthly_price.toLocaleString()} × {plan.paid_months} months</div>
                        </>
                      ) : (
                        <div className="text-center py-2">
                          <p className="text-sm font-medium" style={{ color: '#78716C' }}>Price will be updated soon</p>
                          <p className="text-xs mt-1" style={{ color: '#A8A29E' }}>Contact us on WhatsApp for details</p>
                        </div>
                      )}
                    </div>

                    {/* Offer Highlight */}
                    {plan.bonus_months > 0 && (
                      <div className="flex items-center gap-2 mb-4 p-3 rounded-lg" style={{ backgroundColor: '#FEF3C7' }} data-testid={`bonus-badge-${plan.id}`}>
                        <Gift className="h-5 w-5 flex-shrink-0" style={{ color: '#D97706' }} />
                        <span className="text-sm font-semibold" style={{ color: '#92400E' }}>
                          Pay for {plan.paid_months} Months — Get {plan.bonus_months} Bonus Month{plan.bonus_months > 1 ? 's' : ''} FREE!
                        </span>
                      </div>
                    )}

                    {/* Book Now */}
                    <Button onClick={() => handleBookNow(plan)} className="w-full py-3 rounded-xl font-semibold text-white text-base mb-3" style={{ backgroundColor: color }} data-testid={`sub-book-${plan.id}`}>
                      Book Annual Subscription
                    </Button>

                    {/* Collapsible Benefits */}
                    <button
                      onClick={() => setExpandedCard(isExpanded ? null : plan.id)}
                      className="w-full flex items-center justify-center gap-1.5 py-2 text-sm font-medium transition-colors rounded-lg hover:bg-gray-50"
                      style={{ color: '#78716C' }}
                      data-testid={`benefits-toggle-${plan.id}`}
                    >
                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      {isExpanded ? 'Hide Details' : 'View All Benefits & Support'}
                    </button>

                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t space-y-3" style={{ borderColor: '#E7E5E4' }} data-testid={`benefits-content-${plan.id}`}>
                        <div className="flex items-start gap-2.5">
                          <Star className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color }} />
                          <div>
                            <p className="text-sm font-medium" style={{ color: '#1C1917' }}>Annual Subscription Benefits</p>
                            <p className="text-xs" style={{ color: '#78716C' }}>
                              {plan.total_months} months of monthly {plan.title} pooja at a discounted rate
                              {plan.savings > 0 && `. Save ₹${plan.savings.toLocaleString()} compared to monthly booking.`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2.5">
                          <Gift className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: '#D97706' }} />
                          <div>
                            <p className="text-sm font-medium" style={{ color: '#1C1917' }}>{plan.bonus_months} Bonus Months</p>
                            <p className="text-xs" style={{ color: '#78716C' }}>Get {plan.bonus_months} extra month{plan.bonus_months > 1 ? 's' : ''} of pooja completely free when you subscribe annually</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2.5">
                          <Bell className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: '#059669' }} />
                          <div>
                            <p className="text-sm font-medium" style={{ color: '#1C1917' }}>Monthly Nakshatra Pooja Reminders</p>
                            <p className="text-xs" style={{ color: '#78716C' }}>We'll remind you before each monthly pooja via WhatsApp based on your Nakshatra</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2.5">
                          <MessageCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: '#25D366' }} />
                          <div>
                            <p className="text-sm font-medium" style={{ color: '#1C1917' }}>Dedicated WhatsApp Support</p>
                            <p className="text-xs" style={{ color: '#78716C' }}>Book, reschedule, or get help anytime via our WhatsApp chat support</p>
                          </div>
                        </div>
                        <div className="p-3 rounded-lg text-xs leading-relaxed" style={{ backgroundColor: '#FEF2F2', color: '#991B1B' }}>
                          No cash payment at the time of booking. Payment instructions will be shared via WhatsApp.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Payment Note */}
      <div className="max-w-5xl mx-auto px-4 pb-8">
        <div className="text-center p-4 rounded-xl" style={{ backgroundColor: '#FEF3C7', border: '1px solid #FCD34D' }} data-testid="payment-note">
          <p className="text-sm font-medium" style={{ color: '#92400E' }}>
            No cash payment at the time of booking. Payment instructions will be shared via WhatsApp.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t" style={{ borderColor: '#E7E5E4', backgroundColor: '#F5F5F4' }}>
        <div className="max-w-3xl mx-auto px-4 py-8 text-center space-y-3">
          <p className="text-xs leading-relaxed" style={{ color: '#78716C' }}>Prayan Pilgrim Services is an independent private service and is not the official website or representative of Sree Parashurama Temple or its Devaswom. All bookings and services are subject to the rules, timings, availability, and decisions of the Temple authorities.</p>
        </div>
      </div>
    </div>
  );
}
